import { Injectable } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { NotificationService } from '../notification/notification.service';
import {
  ServiceOrderService,
  SidebarCountersResponse,
} from './service-order.service';
import { SessionPermissionService } from '../auth/session-permission.service';

export interface SidebarCountersState {
  serviceOrdersActionPendingTotal: number;
  approvalsTotal: number;
}

const INITIAL_COUNTERS_STATE: SidebarCountersState = {
  serviceOrdersActionPendingTotal: 0,
  approvalsTotal: 0,
};

@Injectable({
  providedIn: 'root',
})
export class SidebarCountersService {
  readonly counters$ = new BehaviorSubject<SidebarCountersState>(
    INITIAL_COUNTERS_STATE,
  );

  private initialized = false;
  private active = false;
  private updateSubscription?: Subscription;
  private latestVersion = -1;

  constructor(
    private readonly serviceOrderService: ServiceOrderService,
    private readonly notificationService: NotificationService,
    private readonly sessionPermissionService: SessionPermissionService,
  ) {}

  setActive(active: boolean): void {
    if (this.sessionPermissionService.getTypeTenant() !== 'Tomador') {
      return;
    }

    if (active) {
      this.activate();
      return;
    }

    this.deactivate();
  }

  destroy(): void {
    this.deactivate();
    this.initialized = false;
    this.latestVersion = -1;
    this.counters$.next(INITIAL_COUNTERS_STATE);
  }

  private activate(): void {
    if (this.active) {
      return;
    }

    const mustLoadInitialSnapshot = !this.initialized;
    if (mustLoadInitialSnapshot) {
      this.initialized = true;
      this.notificationService.connect();
    }

    this.active = true;
    this.updateSubscription = this.notificationService
      .listenToSidebarCounterUpdates()
      .subscribe({
        next: (event) => {
          if (event.version <= this.latestVersion) return;

          this.latestVersion = event.version;
          this.counters$.next({
            serviceOrdersActionPendingTotal:
              event.serviceOrdersActionPendingTotal,
            approvalsTotal: event.approvalsTotal,
          });
        },
      });

    if (mustLoadInitialSnapshot) {
      this.load();
    }
  }

  private deactivate(): void {
    this.active = false;
    this.updateSubscription?.unsubscribe();
    this.updateSubscription = undefined;
  }

  private load(): void {
    this.serviceOrderService.getSidebarCounters().subscribe({
      next: (response) => {
        const responseVersion = response.version ?? 0;
        if (responseVersion < this.latestVersion) return;

        this.latestVersion = responseVersion;
        this.counters$.next(this.mapCountersResponse(response));
      },
      error: () => {
        if (this.latestVersion < 0) {
          this.counters$.next(INITIAL_COUNTERS_STATE);
        }
      },
    });
  }

  private mapCountersResponse(
    response: SidebarCountersResponse,
  ): SidebarCountersState {
    return {
      serviceOrdersActionPendingTotal:
        response.programs?.serviceOrders?.actionPendingTotal ?? 0,
      approvalsTotal: response.programs?.approvals?.total ?? 0,
    };
  }
}
